/* components/NoticeTable.tsx */
import React from 'react';
import { Table, Space, Popconfirm, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Notice } from '../../../Modules/Notice/Model/Notice';
import dayjs from 'dayjs';
import { getAccountNameById } from '../../../Utils/getAccountNameById';
import { useAppDispatch, useAppSelector } from '../../../Modules/Presentation/hooks';
import { selectAccounts } from '../../../Modules/Account/Presentation/selectors';
import useOnce from '../../../Utils/hook/useOnce';
import { fetchAccounts } from '../../../Modules/Account/Presentation/actions';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import { stripNoticeContentPreview } from '../../../Utils/stripNoticePreview';

interface Props {
  data: Notice[];
  total: number;
  perPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onEdit?: (n: Notice) => void;
  onDelete?: (n: Notice) => void;
  onView?: (n: Notice) => void;
  showActions?: boolean;
}

const NoticeTable = ({
  data, total, perPage, currentPage, onPageChange,
  onEdit, onDelete, onView, showActions = false,
}: Props) => {
  const dispatch = useAppDispatch();
  const accountList = useAppSelector(selectAccounts);
  const currentUser = useAppSelector(selectUser)

  useOnce(() => {
    if (accountList.length === 0) {
      dispatch(fetchAccounts());
    }
  })

  const columns: ColumnsType<Notice> = [
    {
      title: '순번',
      dataIndex: 'noticeId',
      width: 80,
      render: (_1, _2, i) => (currentPage - 1) * perPage + i + 1,
    },
    { title: '제목', dataIndex: 'title', render: t => <Tooltip title={t}>{t}</Tooltip> },
    {
      title: '내용',
      dataIndex: 'content',
      ellipsis: true,
      render: (text: string) => {
        const plain = stripNoticeContentPreview(text);
        return (
          <Tooltip title={plain || undefined}>
            <span>{plain}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '작성일',
      dataIndex: 'created_at',
      width: 180,
      render: (v: string) =>
        dayjs(v).isValid()
          ? dayjs(v).format('YYYY-MM-DD HH:mm:ss')
          : '-',
    },
    { title: '작성자', dataIndex: 'authorId', render: (id: number) => getAccountNameById(accountList, id,false, currentUser), },
  ];

  if (showActions) {
    columns.push({
      title: '관리',
      width: 120,
      render: (_1, rec) => (
        <Space size="large">
          <a
            onClick={e => {
              e.stopPropagation();
              onEdit?.(rec);
            }}
          >
            수정
          </a>

          <Popconfirm
            title="삭제하시겠습니까?"
            okText="예" cancelText="아니오"
            onConfirm={e => {
              e?.stopPropagation();
              onDelete?.(rec);
            }}
            onPopupClick={e => e.stopPropagation()}
          >
            <a
              style={{ color: 'red' }}
              onClick={e => e.stopPropagation()}
            >
              삭제
            </a>
          </Popconfirm>
        </Space>
      ),
    });
  }

  return (
    <Table
      rowKey="noticeId"
      dataSource={data}
      columns={columns}
      onRow={rec => ({
        onClick: () => onView?.(rec),
      })}
      pagination={{
        current: currentPage,
        pageSize: perPage,
        total,
        onChange: onPageChange,
        showSizeChanger: false,
      }}
      locale={{ emptyText: '조회된 데이터가 없습니다.' }}
      style={{ cursor: onView ? 'pointer' : undefined }}
      scroll={{ x: 'max-content' }}
    />
  );
};

export default NoticeTable;
